import type { LegalLifecycle } from '../../lib/legal-versions.js';
import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

/* Copy dos COMPONENTES de documentos legais (ADR-0774): campos por idioma, dialog de publicação,
   modal de criar/editar versão e a tabela de versões. O header/toolbar/toasts da PÁGINA ficam em
   `content/sections/legal.ts` (frente de routes/) — este arquivo é só o que `components/legal/`
   desenha por conta própria. O mapeamento de erro (`legalErrorMessage`) vive em `legal-error.ts`
   com um parâmetro `locale?` opcional pelo mesmo motivo de `releases-error.ts`. */
export interface LegalAdminContent {
  readonly localeFields: {
    readonly titleLabel: string;
    readonly titlePlaceholder: string;
    readonly contentLabel: string;
    readonly previewAriaLabel: (locale: string) => string;
    readonly emptyPreview: string;
    readonly contentPlaceholder: string;
  };
  readonly publishDialog: {
    readonly title: (version: number, docLabel: string) => string;
    readonly description: (version: number) => string;
    readonly localesWarning: (locales: string) => string;
    readonly timingNow: string;
    readonly timingScheduled: string;
    readonly effectiveAtLabel: string;
    readonly scheduledHint: string;
    readonly cancel: string;
    readonly confirm: (version: number) => string;
  };
  readonly versionFormModal: {
    readonly manualSource: string;
    readonly localeLabels: Readonly<Record<string, string>>;
    readonly titleNew: string;
    readonly titlePublishedReadOnly: (version: number) => string;
    readonly titleDraftReadOnly: (version: number) => string;
    readonly titleEditDraft: (version: number) => string;
    readonly missingFieldsError: string;
    readonly emptyFieldsError: string;
    readonly draftCreatedToast: string;
    readonly draftCreatedWithVersionToast: (version: number) => string;
    readonly draftSavedToast: string;
    readonly readOnlyDescription: string;
    readonly editableDescription: string;
    readonly publishedWarning: string;
    readonly missingFallbackWarning: (fallbackLocale: string) => string;
    readonly missingOtherWarning: (missing: string, fallbackLocale: string) => string;
    readonly sourceLabel: string;
    readonly sourceAriaLabel: string;
    readonly writeNowOption: string;
    readonly copyFromVersionOption: (version: number) => string;
    readonly copyingInfo: (source: string) => string;
    readonly showMarkdown: string;
    readonly editMarkdown: string;
    readonly preview: string;
    readonly close: string;
    readonly cancel: string;
    readonly saveDraft: string;
    readonly createDraft: string;
  };
  readonly versionsTable: {
    readonly lifecycleLabels: Readonly<Record<LegalLifecycle, string>>;
    readonly emptyTitle: string;
    readonly emptyDescription: string;
    readonly createButton: string;
    readonly columnVersion: string;
    readonly columnTitle: string;
    readonly columnSituation: string;
    readonly columnLanguages: string;
    readonly columnEffectiveAt: string;
    readonly columnPublishedAt: string;
    readonly columnActions: string;
    readonly published: string;
    readonly draft: string;
    readonly edit: string;
    readonly publish: string;
    readonly remove: string;
    readonly view: string;
    readonly copyFromThis: string;
    readonly selectPageAriaLabel: string;
    readonly selectRowAriaLabel: (version: number) => string;
  };
}

const LEGAL_ADMIN_CONTENT: Bilingual<LegalAdminContent> = {
  'pt-BR': {
    localeFields: {
      titleLabel: 'Título',
      titlePlaceholder: 'Termos de Uso',
      contentLabel: 'Conteúdo (markdown)',
      previewAriaLabel: (locale) => `Pré-visualização do conteúdo em ${locale}`,
      emptyPreview: 'Sem conteúdo para pré-visualizar.',
      contentPlaceholder: '# Termos de Uso\n\n> [!WARNING]\n> Rascunho estrutural…',
    },
    publishDialog: {
      title: (version, docLabel) => `Publicar versão ${version} de ${docLabel}`,
      description: (version) =>
        `Publicar torna esta versão IMUTÁVEL e é irreversível. Corrigir o texto depois exige criar a versão ${version} + 1 — a publicada continua no histórico.`,
      localesWarning: (locales) =>
        `Idiomas nesta versão: ${locales}. Todos serão publicados juntos — a versão é publicada por inteiro, não por idioma.`,
      timingNow: 'Vigorar imediatamente',
      timingScheduled: 'Vigorar em data futura (aviso prévio)',
      effectiveAtLabel: 'Data e hora de vigência',
      scheduledHint:
        'A versão fica publicada e registrada, mas o app e o site continuam servindo a versão anterior até esta data.',
      cancel: 'Cancelar',
      confirm: (version) => `Publicar versão ${version}`,
    },
    versionFormModal: {
      manualSource: 'Escrever agora',
      localeLabels: { 'pt-BR': 'Português (BR)', 'en-US': 'English (US)' },
      titleNew: 'Nova versão',
      titlePublishedReadOnly: (version) => `Versão ${version} (publicada — somente leitura)`,
      titleDraftReadOnly: (version) => `Rascunho da versão ${version} (somente leitura)`,
      titleEditDraft: (version) => `Editar rascunho da versão ${version}`,
      missingFieldsError:
        'Preencha título e conteúdo de pelo menos um idioma, ou escolha uma versão para copiar.',
      emptyFieldsError: 'Título e conteúdo não podem ficar vazios.',
      draftCreatedToast: 'Rascunho criado.',
      draftCreatedWithVersionToast: (version) =>
        `Rascunho da versão ${version} criado. Publique quando o texto estiver revisado.`,
      draftSavedToast: 'Rascunho salvo.',
      readOnlyDescription:
        'Texto publicado não é editável. Para corrigir, crie a versão seguinte a partir desta.',
      editableDescription:
        'O número da versão é atribuído pelo servidor ao salvar. A versão nasce como rascunho e só vale para o público depois de publicada.',
      publishedWarning:
        'Esta versão já vigorou ou está publicada — o histórico do que a empresa afirmou não é reescrito.',
      missingFallbackWarning: (fallbackLocale) =>
        `Falta ${fallbackLocale} nesta versão. Sem esse idioma a leitura pública não tem para onde cair e o documento fica indisponível — a publicação será recusada. Preencha ${fallbackLocale} antes de publicar.`,
      missingOtherWarning: (missing, fallbackLocale) =>
        `Idioma sem tradução nesta versão: ${missing}. A leitura pública cai para ${fallbackLocale} dentro da MESMA versão (nunca rebaixa a versão). Para incluir o idioma, crie uma nova versão com os dois preenchidos.`,
      sourceLabel: 'Origem do conteúdo',
      sourceAriaLabel: 'Origem do conteúdo',
      writeNowOption: 'Escrever agora',
      copyFromVersionOption: (version) => `Copiar da versão ${version}`,
      copyingInfo: (source) =>
        `O conteúdo dos idiomas da versão ${source} será copiado para a nova versão, que nasce como rascunho. Depois de criar, edite o texto e publique.`,
      showMarkdown: 'Ver markdown',
      editMarkdown: 'Editar markdown',
      preview: 'Pré-visualizar',
      close: 'Fechar',
      cancel: 'Cancelar',
      saveDraft: 'Salvar rascunho',
      createDraft: 'Criar rascunho',
    },
    versionsTable: {
      lifecycleLabels: {
        draft: 'Rascunho',
        scheduled: 'Vigência programada',
        current: 'Em vigor',
        superseded: 'Substituída',
      },
      emptyTitle: 'Nenhuma versão ainda',
      emptyDescription:
        'Este documento não tem nenhuma versão registrada. Crie a primeira para que o app e o site tenham texto a exibir.',
      createButton: 'Nova versão',
      columnVersion: 'Versão',
      columnTitle: 'Título',
      columnSituation: 'Situação',
      columnLanguages: 'Idiomas',
      columnEffectiveAt: 'Vigora em',
      columnPublishedAt: 'Publicado em',
      columnActions: 'Ações',
      published: 'Publicado',
      draft: 'Rascunho',
      edit: 'Editar',
      publish: 'Publicar',
      remove: 'Remover',
      view: 'Ver',
      copyFromThis: 'Nova versão a partir desta',
      selectPageAriaLabel: 'Selecionar rascunhos visíveis',
      selectRowAriaLabel: (version) => `Selecionar rascunho da versão ${version}`,
    },
  },
  'en-US': {
    localeFields: {
      titleLabel: 'Title',
      titlePlaceholder: 'Terms of Service',
      contentLabel: 'Content (markdown)',
      previewAriaLabel: (locale) => `Content preview in ${locale}`,
      emptyPreview: 'No content to preview.',
      contentPlaceholder: '# Terms of Service\n\n> [!WARNING]\n> Structural draft…',
    },
    publishDialog: {
      title: (version, docLabel) => `Publish version ${version} of ${docLabel}`,
      description: (version) =>
        `Publishing makes this version IMMUTABLE and is irreversible. Fixing the text afterwards requires creating version ${version} + 1 — the published one remains in the history.`,
      localesWarning: (locales) =>
        `Languages in this version: ${locales}. All will be published together — the version is published as a whole, not per language.`,
      timingNow: 'Take effect immediately',
      timingScheduled: 'Take effect on a future date (advance notice)',
      effectiveAtLabel: 'Effective date and time',
      scheduledHint:
        'The version is published and recorded, but the app and website keep serving the previous version until this date.',
      cancel: 'Cancel',
      confirm: (version) => `Publish version ${version}`,
    },
    versionFormModal: {
      manualSource: 'Write now',
      localeLabels: { 'pt-BR': 'Português (BR)', 'en-US': 'English (US)' },
      titleNew: 'New version',
      titlePublishedReadOnly: (version) => `Version ${version} (published — read-only)`,
      titleDraftReadOnly: (version) => `Draft of version ${version} (read-only)`,
      titleEditDraft: (version) => `Edit draft of version ${version}`,
      missingFieldsError:
        'Fill in title and content for at least one language, or choose a version to copy.',
      emptyFieldsError: 'Title and content cannot be empty.',
      draftCreatedToast: 'Draft created.',
      draftCreatedWithVersionToast: (version) =>
        `Draft of version ${version} created. Publish once the text has been reviewed.`,
      draftSavedToast: 'Draft saved.',
      readOnlyDescription:
        'Published text is not editable. To fix it, create the next version from this one.',
      editableDescription:
        'The version number is assigned by the server on save. The version starts as a draft and only applies to the public once published.',
      publishedWarning:
        'This version was in effect or is published — the history of what the company stated is never rewritten.',
      missingFallbackWarning: (fallbackLocale) =>
        `Missing ${fallbackLocale} in this version. Without this language, public reads have nowhere to fall back to and the document becomes unavailable — publishing will be refused. Fill in ${fallbackLocale} before publishing.`,
      missingOtherWarning: (missing, fallbackLocale) =>
        `Language without translation in this version: ${missing}. Public reads fall back to ${fallbackLocale} within the SAME version (it never downgrades the version). To include the language, create a new version with both filled in.`,
      sourceLabel: 'Content source',
      sourceAriaLabel: 'Content source',
      writeNowOption: 'Write now',
      copyFromVersionOption: (version) => `Copy from version ${version}`,
      copyingInfo: (source) =>
        `The content of version ${source}'s languages will be copied to the new version, which starts as a draft. After creating it, edit the text and publish.`,
      showMarkdown: 'View markdown',
      editMarkdown: 'Edit markdown',
      preview: 'Preview',
      close: 'Close',
      cancel: 'Cancel',
      saveDraft: 'Save draft',
      createDraft: 'Create draft',
    },
    versionsTable: {
      lifecycleLabels: {
        draft: 'Draft',
        scheduled: 'Scheduled',
        current: 'In effect',
        superseded: 'Superseded',
      },
      emptyTitle: 'No version yet',
      emptyDescription:
        'This document has no version registered yet. Create the first one so the app and website have text to show.',
      createButton: 'New version',
      columnVersion: 'Version',
      columnTitle: 'Title',
      columnSituation: 'Situation',
      columnLanguages: 'Languages',
      columnEffectiveAt: 'Effective on',
      columnPublishedAt: 'Published on',
      columnActions: 'Actions',
      published: 'Published',
      draft: 'Draft',
      edit: 'Edit',
      publish: 'Publish',
      remove: 'Remove',
      view: 'View',
      copyFromThis: 'New version from this one',
      selectPageAriaLabel: 'Select visible drafts',
      selectRowAriaLabel: (version) => `Select draft version ${version}`,
    },
  },
};

export const useLegalAdminContent = (): LegalAdminContent => useBilingual(LEGAL_ADMIN_CONTENT);
